import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles, Star, Quote } from "lucide-react";
import Marquee from "@/components/Marquee";
import CourseCard from "@/components/CourseCard";
import {
  COURSES,
  TESTIMONIALS,
  FOUNDER_IMG,
} from "@/lib/constants";

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO — The Bold Statement */}
      <section
        className="relative bg-[#0A0A0A] text-white overflow-hidden grain"
        data-testid="home-hero"
      >
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-24 md:pb-40 min-h-[88vh] flex flex-col justify-center">
          <div className="flex items-center gap-3 mb-8 fade-up">
            <span className="inline-flex items-center gap-2 bg-[#FFD60A] text-black px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.3em]">
              <Sparkles className="w-3.5 h-3.5" /> AI Certification
            </span>
            <span className="text-[10px] uppercase tracking-[0.3em] text-white/60 font-bold hidden sm:inline">
              School at Night · Cohort 04
            </span>
          </div>

          <h1
            className="font-display uppercase leading-[0.86] text-[15vw] sm:text-7xl md:text-8xl lg:text-[10rem] max-w-5xl fade-up delay-1"
            data-testid="home-hero-headline"
          >
            Stop building <br className="hidden sm:block" />
            <span className="hi-yellow">like it's 2019</span>, sis.
          </h1>

          <p
            className="mt-8 max-w-2xl text-white/80 text-lg md:text-xl leading-relaxed fade-up delay-2"
            data-testid="home-hero-sub"
          >
            You ain't gotta hustle harder — you gotta prompt smarter. AI
            certifications and courses built for the Black woman entrepreneur
            ready to scale without burning out.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4 fade-up delay-3">
            <Link
              to="/book"
              data-testid="home-hero-cta-enroll"
              className="group inline-flex items-center gap-2 bg-[#FFD60A] text-black px-7 py-5 font-bold uppercase tracking-[0.2em] text-sm hover:bg-white transition-colors"
            >
              Enroll Now
              <ArrowUpRight className="w-5 h-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link
              to="/services"
              data-testid="home-hero-cta-services"
              className="inline-flex items-center gap-2 border-2 border-white text-white px-7 py-5 font-bold uppercase tracking-[0.2em] text-sm hover:bg-white hover:text-black transition-colors"
            >
              See the Courses
            </Link>
          </div>

          {/* Trust micro-row */}
          <div className="mt-16 grid grid-cols-3 max-w-xl gap-6 fade-up delay-4 border-t border-[#2A2A2A] pt-8">
            <div>
              <p className="font-display text-3xl md:text-4xl text-[#FFD60A]">2.4k+</p>
              <p className="text-[11px] uppercase tracking-[0.2em] text-white/50 font-bold mt-1">Students Certified</p>
            </div>
            <div>
              <p className="font-display text-3xl md:text-4xl text-[#FFD60A]">4.9★</p>
              <p className="text-[11px] uppercase tracking-[0.2em] text-white/50 font-bold mt-1">Cohort Rating</p>
            </div>
            <div>
              <p className="font-display text-3xl md:text-4xl text-[#FFD60A]">$5M+</p>
              <p className="text-[11px] uppercase tracking-[0.2em] text-white/50 font-bold mt-1">Alumni Revenue</p>
            </div>
          </div>
        </div>
      </section>

      {/* COURSE CARDS BAND */}
      <section
        className="bg-[#FAFAFA] py-20 md:py-28"
        data-testid="home-courses"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <p className="text-[11px] uppercase tracking-[0.3em] text-black/50 font-bold mb-3">
                The Curriculum
              </p>
              <h2 className="font-display uppercase text-5xl md:text-6xl leading-[0.95] max-w-2xl">
                Pick your <span className="hi-yellow">power move.</span>
              </h2>
            </div>
            <p className="max-w-md text-black/70 text-base md:text-lg leading-relaxed">
              Three drops. Real curriculum. No fluff. Every course is built so
              you walk out with a working skill — not just a screenshot.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {COURSES.map((c, i) => (
              <CourseCard key={c.id} course={c} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <Marquee />

      {/* ABOUT FOUNDER */}
      <section
        className="bg-[#FAFAFA] py-20 md:py-32"
        data-testid="home-founder"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
          <div className="md:col-span-5 relative">
            <div className="relative">
              <img
                src={FOUNDER_IMG}
                alt="Professor Kez"
                className="w-full h-[520px] md:h-[620px] object-cover object-center grayscale-0"
              />
              <div className="absolute -bottom-5 -right-5 hidden md:block bg-[#FFD60A] px-5 py-3">
                <p className="font-display text-2xl uppercase">CEO · Educator</p>
              </div>
            </div>
          </div>

          <div className="md:col-span-7 md:pl-6">
            <p className="text-[11px] uppercase tracking-[0.3em] text-black/50 font-bold mb-4">
              Meet the Founder
            </p>
            <h2 className="font-display uppercase text-5xl md:text-6xl lg:text-7xl leading-[0.92]">
              Hey, I'm <span className="hi-yellow">Professor Kez</span>.
            </h2>
            <div className="mt-8 space-y-5 text-lg leading-relaxed text-black/80 max-w-xl">
              <p>
                I built SkillhouseU for the woman who's tired of writing
                business plans nobody reads. The one juggling a 9-to-5, the
                kids' games, and that idea she keeps swiping in her Notes app.
              </p>
              <p>
                We don't do fluff webinars. We do frameworks, prompt libraries,
                and certifications that actually mean something in the room
                where decisions get made.
              </p>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/book"
                data-testid="founder-cta-book"
                className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-6 py-4 font-bold uppercase tracking-[0.2em] text-sm hover:bg-[#FFD60A] hover:text-black transition-colors"
              >
                Work With Kez <ArrowUpRight className="w-4 h-4" />
              </Link>
              <span className="text-[11px] uppercase tracking-[0.3em] text-black/50 font-bold">
                As mentioned in · Blavity · Forbes · Essence
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section
        className="relative bg-[#0A0A0A] text-white py-24 md:py-32 overflow-hidden grain"
        data-testid="home-testimonials"
      >
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <p className="text-[11px] uppercase tracking-[0.3em] text-[#FFD60A] font-bold mb-3">
                The Receipts
              </p>
              <h2 className="font-display uppercase text-5xl md:text-7xl leading-[0.92] max-w-3xl">
                Sis, this <span className="hi-yellow">changed</span> everything.
              </h2>
            </div>
            <div className="flex items-center gap-1 text-[#FFD60A]">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-[#FFD60A]" />
              ))}
              <span className="ml-3 text-white/80 text-sm">4.9 average · 200+ reviews</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <article
                key={t.name}
                data-testid={`testimonial-${i}`}
                className="bg-[#141414] border border-[#2A2A2A] p-8 md:p-10 flex flex-col justify-between hover:border-[#FFD60A] transition-colors"
              >
                <div>
                  <Quote className="w-8 h-8 text-[#FFD60A] mb-6" />
                  <p className="text-lg leading-relaxed text-white/90">
                    "{t.quote}"
                  </p>
                </div>
                <div className="mt-8 flex items-center gap-4 pt-6 border-t border-[#2A2A2A]">
                  <img
                    src={t.image}
                    alt={t.name}
                    className="w-12 h-12 rounded-full object-cover border-2 border-[#FFD60A]"
                  />
                  <div>
                    <p className="font-bold">{t.name}</p>
                    <p className="text-[11px] uppercase tracking-[0.2em] text-white/50">
                      {t.role} · {t.city}
                    </p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA BAND */}
      <section
        className="bg-[#FFD60A] text-black py-20 md:py-28"
        data-testid="home-final-cta"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-10">
          <h2 className="font-display uppercase text-5xl md:text-7xl leading-[0.9] max-w-3xl">
            Doors close <br className="hidden md:block" />
            when seats fill.
          </h2>
          <Link
            to="/book"
            data-testid="home-final-cta-btn"
            className="inline-flex items-center gap-2 bg-black text-[#FFD60A] px-8 py-5 font-bold uppercase tracking-[0.2em] text-sm hover:bg-white hover:text-black transition-colors"
          >
            Book Your Spot <ArrowUpRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
